import * as Sentry from '@sentry/nextjs';

import HTTPError from './HTTPError';
import * as AuthService from './AuthService';
import { getApiUrlFromRoute } from './utils';

// generates a response object with only the json method
const apiResponse = response => ({
  json: () => response.json(),
  download: async filename => {
    const content = await response.blob('application/pdf');
    content.name = filename;
    if (/CriOS/.test(navigator.userAgent)) {
      const reader = new FileReader();
      reader.onload = () => {
        window.open(reader.result);
      };
      reader.readAsDataURL(content);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(content);
      link.download = filename;
      link.click();
    }
  },
  display: async filename => {
    const content = await response.blob('application/pdf');
    content.name = filename;
    if (/CriOS/.test(navigator.userAgent)) {
      const reader = new FileReader();
      reader.onload = () => {
        window.open(reader.result);
      };
      reader.readAsDataURL(content);
    }
    return window.URL.createObjectURL(content);
  },
});

// adds authorization to header
const withAuthorization = (headers = {}) => {
  if ('Authorization' in headers) {
    return headers;
  }
  const auth = AuthService.getAuth();
  return auth && auth.accessToken
    ? {
        ...headers,
        Authorization: `Bearer ${auth.accessToken}`,
      }
    : headers;
};

const raw = async (route, init = {}) => {
  const url = getApiUrlFromRoute(route);

  try {
    return await window.fetch(url, { ...init, headers: withAuthorization(init.headers) });
  } catch (error) {
    Sentry.configureScope(scope => {
      scope.setExtra('api:fetchError', error.message);
    });

    throw new HTTPError({
      code: 'failed_to_fetch',
      detail: 'Failed to fetch',
    });
  }
};

export const isJsonResponse = response => {
  // used to ensure that the response is JSON
  const contentType = response.headers.get('content-type');
  return contentType ? contentType.includes('application/json') : false;
};

// Verifies if body represents an authentication error
// The typical API payload is:
// {
//   code: { code: 'token_not_valid', message: 'token_not_valid' },
//   detail: 'Given token not valid for any token type',
// }
const isInvalidToken = body => [body?.code, body?.code?.code].includes('token_not_valid');

const isRefreshTokenRoute = route => /\/v1\/auth\/token\/refresh\//.test(route);

const handleError = async (body, route, options) => {
  try {
    if (isInvalidToken(body, route) && !isRefreshTokenRoute(route)) {
      await AuthService.refresh();
      const response = await raw(route, options);
      if (response.ok) {
        return apiResponse(response);
      }
      const responseBody = await response.json();
      throw new HTTPError(responseBody);
    }
  } catch (error) {
    Sentry.withScope(scope => {
      scope.setLevel('error');
      Sentry.captureMessage(`[APIClient handleError] error: ${error.toString()}`);
    });
    throw error;
  }
  throw new HTTPError(body);
};

const fetchApi = async (route, params, verb) => {
  const options = {
    body: params ? JSON.stringify(params) : undefined,
    method: verb,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  };

  if (!isRefreshTokenRoute(route)) {
    await AuthService.tryRefresh(); // This will refresh token only if necessary according to expriation date stored in the JWT token
  }

  const response = await raw(route, options);
  const jsonResponse = isJsonResponse(response);

  if (response.ok && jsonResponse) {
    return apiResponse(response);
  }

  let body;
  if (jsonResponse) {
    body = await response.json();
  } else {
    body = {
      code: 'not_a_json',
      detail: 'Expected a JSON response.',
      contentType: response.headers.get('content-type'),
    };
  }

  Sentry.configureScope(scope => {
    scope.setExtra('api:error', {
      verb,
      url: route,
      status: response.status,
      statusText: response.statusText,
      body,
    });
  });

  return handleError(body, route, options);
};

const fetchFile = async (route, params, verb) => {
  const options = {
    body: params ? JSON.stringify(params) : undefined,
    method: verb,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  };

  const response = await raw(route, options);
  if (response.ok) {
    return apiResponse(response);
  }
  const body = await response.json();
  return handleError(body, route, options);
};

export const get = route => fetchApi(route, undefined, 'GET');
export const getFile = (route, params) => fetchFile(route, params, 'GET');
export const patch = (route, params) => fetchApi(route, params, 'PATCH');
export const put = (route, params) => fetchApi(route, params, 'PUT');
export const post = (route, params) => fetchApi(route, params, 'POST');
export const del = route => fetchApi(route, undefined, 'DELETE');

export const getJson = async route => {
  const response = await get(route);
  return response.json();
};

export const patchJson = async (route, params) => {
  const response = await patch(route, params);
  return response.json();
};

export const postJson = async (route, params) => {
  const response = await post(route, params);
  return response.json();
};
